import { analyticsConfig } from './config'
import type { BaseEventParams, EventName, EventPayloadMap } from './events'
import { ga4Provider } from './providers/ga4'
import { metaPixelProvider } from './providers/meta-pixel'
import type { AnalyticsProvider } from './types'
import { enrichEventParams } from './utils'

class Analytics {
  private providers: AnalyticsProvider[] = []
  private initialized = false

  constructor() {
    // Provider 등록
    this.providers.push(ga4Provider)
    this.providers.push(metaPixelProvider)
  }

  init(): void {
    if (this.initialized) {
      return
    }

    // Analytics가 비활성화된 경우 초기화하지 않음
    if (!analyticsConfig.enabled) {
      if (analyticsConfig.debug) {
        console.log('Analytics is disabled via VITE_ENABLE_ANALYTICS')
      }
      this.initialized = true
      return
    }

    // 모든 provider 초기화
    this.providers.forEach(provider => {
      try {
        provider.init()
      } catch (error) {
        if (analyticsConfig.debug) {
          console.error('Analytics provider init error:', error)
        }
      }
    })

    this.initialized = true
  }

  trackPageView(path: string, title?: string): void {
    if (!analyticsConfig.enabled) {
      return
    }

    this.providers.forEach(provider => {
      try {
        provider.trackPageView(path, title)
      } catch (error) {
        if (analyticsConfig.debug) {
          console.error('Analytics trackPageView error:', error)
        }
      }
    })
  }

  trackEvent<T extends EventName>(
    eventName: T,
    ...args: EventPayloadMap[T] extends BaseEventParams
      ? [parameters?: EventPayloadMap[T]]
      : [parameters: EventPayloadMap[T]]
  ): void {
    if (!analyticsConfig.enabled) {
      return
    }

    // 공통 파라미터 추가
    const parameters = args[0]
    const enrichedParams = parameters ? enrichEventParams(parameters) : enrichEventParams({})

    this.providers.forEach(provider => {
      try {
        provider.trackEvent(eventName, enrichedParams as Record<string, unknown>)
      } catch (error) {
        if (analyticsConfig.debug) {
          console.error('Analytics trackEvent error:', error)
        }
      }
    })
  }

  setUserId(userId: string | null): void {
    if (!analyticsConfig.enabled) {
      return
    }

    this.providers.forEach(provider => {
      try {
        provider.setUserId(userId)
      } catch (error) {
        if (analyticsConfig.debug) {
          console.error('Analytics setUserId error:', error)
        }
      }
    })
  }

  setUserProperties(properties: Record<string, unknown>): void {
    if (!analyticsConfig.enabled) {
      return
    }

    this.providers.forEach(provider => {
      try {
        provider.setUserProperties(properties)
      } catch (error) {
        if (analyticsConfig.debug) {
          console.error('Analytics setUserProperties error:', error)
        }
      }
    })
  }
}

// 싱글톤 인스턴스
export const analytics = new Analytics()

// hooks 내보내기
export { usePageTracking } from './hooks/usePageTracking'

// 이벤트 상수 및 타입 내보내기
export * from './events'

// 유틸리티 함수 내보내기
export * from './utils'
